/**
 * Copyright (c) 2019 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __assign } from "tslib";
import { ParamDefinition as PD } from '../../../mol-util/param-definition';
import { Vec3 } from '../../../mol-math/linear-algebra';
import { Unit } from '../../../mol-model/structure';
import { Mesh } from '../../../mol-geo/geometry/mesh/mesh';
import { MeshBuilder } from '../../../mol-geo/geometry/mesh/mesh-builder';
import { Segmentation } from '../../../mol-data/int';
import { isNucleic, isPurineBase, isPyrimidineBase } from '../../../mol-model/structure/model/types';
import { addCylinder } from '../../../mol-geo/geometry/mesh/builder/cylinder';
import { addSphere } from '../../../mol-geo/geometry/mesh/builder/sphere';
import { UnitsMeshParams, UnitsMeshVisual } from '../units-visual';
import { NucleotideLocationIterator, getNucleotideElementLoci, eachNucleotideElement } from './util/nucleotide';
import { BaseGeometry } from '../../../mol-geo/geometry/base';
import { Sphere3D } from '../../../mol-math/geometry';
// TODO support rings for multiple locations (including from microheterogeneity)
var pTrace = Vec3.zero();
var pN1 = Vec3.zero();
var pC2 = Vec3.zero();
var pN3 = Vec3.zero();
var pC4 = Vec3.zero();
var pC5 = Vec3.zero();
var pC6 = Vec3.zero();
var pN7 = Vec3.zero();
var pC8 = Vec3.zero();
var pN9 = Vec3.zero();
var normal = Vec3.zero();
export var NucleotideRingMeshParams = {
    sizeFactor: PD.Numeric(0.2, { min: 0, max: 10, step: 0.01 }),
    radialSegments: PD.Numeric(16, { min: 2, max: 56, step: 2 }, BaseGeometry.CustomQualityParamInfo),
    detail: PD.Numeric(0, { min: 0, max: 3, step: 1 }, BaseGeometry.CustomQualityParamInfo),
};
export var DefaultNucleotideRingMeshProps = PD.getDefaultValues(NucleotideRingMeshParams);
var positionsRing5_6 = new Float32Array(2 * 9 * 3);
var stripIndicesRing5_6 = new Uint32Array([0, 1, 2, 3, 4, 5, 6, 7, 16, 17, 14, 15, 12, 13, 8, 9, 10, 11, 0, 1]);
var fanIndicesTopRing5_6 = new Uint32Array([8, 12, 14, 16, 6, 4, 2, 0, 10]);
var fanIndicesBottomRing5_6 = new Uint32Array([9, 11, 1, 3, 5, 7, 17, 15, 13]);
var positionsRing6 = new Float32Array(2 * 6 * 3);
var stripIndicesRing6 = new Uint32Array([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 0, 1]);
var fanIndicesTopRing6 = new Uint32Array([0, 10, 8, 6, 4, 2]);
var fanIndicesBottomRing6 = new Uint32Array([1, 3, 5, 7, 9, 11]);
var tmpShiftV = Vec3.zero();
function shiftPositions(out, dir) {
    var positions = [];
    for (var _i = 2; _i < arguments.length; _i++) {
        positions[_i - 2] = arguments[_i];
    }
    for (var i = 0, il = positions.length; i < il; ++i) {
        var v = positions[i];
        Vec3.toArray(Vec3.add(tmpShiftV, v, dir), out, (i * 2) * 3);
        Vec3.toArray(Vec3.sub(tmpShiftV, v, dir), out, (i * 2 + 1) * 3);
    }
}
function createNucleotideRingMesh(ctx, unit, structure, theme, props, mesh) {
    if (!Unit.isAtomic(unit))
        return Mesh.createEmpty(mesh);
    var nucleotideElementCount = unit.nucleotideElements.length;
    if (!nucleotideElementCount)
        return Mesh.createEmpty(mesh);
    var sizeFactor = props.sizeFactor, radialSegments = props.radialSegments, detail = props.detail;
    var vertexCount = nucleotideElementCount * (26 + radialSegments * 2);
    var builderState = MeshBuilder.createState(vertexCount, vertexCount / 4, mesh);
    var elements = unit.elements, model = unit.model;
    var _a = model.atomicHierarchy, chainAtomSegments = _a.chainAtomSegments, residueAtomSegments = _a.residueAtomSegments, atoms = _a.atoms, atomicIndex = _a.index;
    var _b = model.atomicHierarchy.derived.residue, moleculeType = _b.moleculeType, traceElementIndex = _b.traceElementIndex;
    var label_comp_id = atoms.label_comp_id;
    var pos = unit.conformation.invariantPosition;
    var chainIt = Segmentation.transientSegments(chainAtomSegments, elements);
    var residueIt = Segmentation.transientSegments(residueAtomSegments, elements);
    var radius = 1 * sizeFactor;
    var halfThickness = 1.25 * sizeFactor;
    var cylinderProps = { radiusTop: 1 * sizeFactor, radiusBottom: 1 * sizeFactor, radialSegments: radialSegments };
    var i = 0;
    while (chainIt.hasNext) {
        residueIt.setSegment(chainIt.move());
        while (residueIt.hasNext) {
            var residueIndex = residueIt.move().index;
            if (isNucleic(moleculeType[residueIndex])) {
                var compId = label_comp_id.value(residueAtomSegments.offsets[residueIndex]);
                var idxTrace = -1, idxN1 = -1, idxC2 = -1, idxN3 = -1, idxC4 = -1, idxC5 = -1, idxC6 = -1, idxN7 = -1, idxC8 = -1, idxN9 = -1;
                builderState.currentGroup = i;
                var isPurine = isPurineBase(compId);
                var isPyrimidine = isPyrimidineBase(compId);
                if (!isPurine && !isPyrimidine) {
                    // detect Purine or Pyrimidin based on geometry
                    var idxC4_1 = atomicIndex.findAtomOnResidue(residueIndex, 'C4');
                    var idxN9_1 = atomicIndex.findAtomOnResidue(residueIndex, 'N9');
                    if (idxC4_1 !== -1 && idxN9_1 !== -1 && Vec3.distance(pos(idxC4_1, pC4), pos(idxN9_1, pN9)) < 1.6) {
                        isPurine = true;
                    }
                    else {
                        isPyrimidine = true;
                    }
                }
                if (isPurine) {
                    idxTrace = traceElementIndex[residueIndex];
                    idxN1 = atomicIndex.findAtomOnResidue(residueIndex, 'N1');
                    idxC2 = atomicIndex.findAtomOnResidue(residueIndex, 'C2');
                    idxN3 = atomicIndex.findAtomOnResidue(residueIndex, 'N3');
                    idxC4 = atomicIndex.findAtomOnResidue(residueIndex, 'C4');
                    idxC5 = atomicIndex.findAtomOnResidue(residueIndex, 'C5');
                    if (idxC5 === -1) {
                        // modified ring, e.g. DP
                        idxC5 = atomicIndex.findAtomOnResidue(residueIndex, 'N5');
                    }
                    idxC6 = atomicIndex.findAtomOnResidue(residueIndex, 'C6');
                    idxN7 = atomicIndex.findAtomOnResidue(residueIndex, 'N7');
                    if (idxN7 === -1) {
                        // modified ring, e.g. DP
                        idxN7 = atomicIndex.findAtomOnResidue(residueIndex, 'C7');
                    }
                    idxC8 = atomicIndex.findAtomOnResidue(residueIndex, 'C8');
                    idxN9 = atomicIndex.findAtomOnResidue(residueIndex, 'N9');
                    if (idxN9 !== -1 && idxTrace !== -1) {
                        pos(idxN9, pN9);
                        pos(idxTrace, pTrace);
                        builderState.currentGroup = i;
                        addCylinder(builderState, pN9, pTrace, 1, cylinderProps);
                        addSphere(builderState, pN9, radius, detail);
                    }
                    if (idxN1 !== -1 && idxC2 !== -1 && idxN3 !== -1 && idxC4 !== -1 && idxC5 !== -1 && idxC6 !== -1 && idxN7 !== -1 && idxC8 !== -1 && idxN9 !== -1) {
                        pos(idxN1, pN1);
                        pos(idxC2, pC2);
                        pos(idxN3, pN3);
                        pos(idxC4, pC4);
                        pos(idxC5, pC5);
                        pos(idxC6, pC6);
                        pos(idxN7, pN7);
                        pos(idxC8, pC8);
                        Vec3.triangleNormal(normal, pN1, pC4, pC5);
                        Vec3.scale(normal, normal, halfThickness);
                        shiftPositions(positionsRing5_6, normal, pN1, pC2, pN3, pC4, pC5, pC6, pN7, pC8, pN9);
                        MeshBuilder.addTriangleStrip(builderState, positionsRing5_6, stripIndicesRing5_6);
                        MeshBuilder.addTriangleFan(builderState, positionsRing5_6, fanIndicesTopRing5_6);
                        MeshBuilder.addTriangleFan(builderState, positionsRing5_6, fanIndicesBottomRing5_6);
                    }
                }
                else if (isPyrimidine) {
                    idxTrace = traceElementIndex[residueIndex];
                    idxN1 = atomicIndex.findAtomOnResidue(residueIndex, 'N1');
                    if (idxN1 === -1) {
                        // modified ring, e.g. DZ
                        idxN1 = atomicIndex.findAtomOnResidue(residueIndex, 'C1');
                    }
                    idxC2 = atomicIndex.findAtomOnResidue(residueIndex, 'C2');
                    idxN3 = atomicIndex.findAtomOnResidue(residueIndex, 'N3');
                    idxC4 = atomicIndex.findAtomOnResidue(residueIndex, 'C4');
                    idxC5 = atomicIndex.findAtomOnResidue(residueIndex, 'C5');
                    idxC6 = atomicIndex.findAtomOnResidue(residueIndex, 'C6');
                    if (idxN1 !== -1 && idxTrace !== -1) {
                        pos(idxN1, pN1);
                        pos(idxTrace, pTrace);
                        builderState.currentGroup = i;
                        addCylinder(builderState, pN1, pTrace, 1, cylinderProps);
                        addSphere(builderState, pN1, radius, detail);
                    }
                    if (idxN1 !== -1 && idxC2 !== -1 && idxN3 !== -1 && idxC4 !== -1 && idxC5 !== -1 && idxC6 !== -1) {
                        pos(idxC2, pC2);
                        pos(idxN3, pN3);
                        pos(idxC4, pC4);
                        pos(idxC5, pC5);
                        pos(idxC6, pC6);
                        Vec3.triangleNormal(normal, pN1, pC4, pC5);
                        Vec3.scale(normal, normal, halfThickness);
                        shiftPositions(positionsRing6, normal, pN1, pC2, pN3, pC4, pC5, pC6);
                        MeshBuilder.addTriangleStrip(builderState, positionsRing6, stripIndicesRing6);
                        MeshBuilder.addTriangleFan(builderState, positionsRing6, fanIndicesTopRing6);
                        MeshBuilder.addTriangleFan(builderState, positionsRing6, fanIndicesBottomRing6);
                    }
                }
                ++i;
            }
        }
    }
    var m = MeshBuilder.getMesh(builderState);
    var sphere = Sphere3D.expand(Sphere3D(), unit.boundary.sphere, 1 * props.sizeFactor);
    m.setBoundingSphere(sphere);
    return m;
}
export var NucleotideRingParams = __assign(__assign({}, UnitsMeshParams), NucleotideRingMeshParams);
export function NucleotideRingVisual(materialId) {
    return UnitsMeshVisual({
        defaultProps: PD.getDefaultValues(NucleotideRingParams),
        createGeometry: createNucleotideRingMesh,
        createLocationIterator: NucleotideLocationIterator.fromGroup,
        getLoci: getNucleotideElementLoci,
        eachLocation: eachNucleotideElement,
        setUpdateState: function (state, newProps, currentProps) {
            state.createGeometry = (newProps.sizeFactor !== currentProps.sizeFactor ||
                newProps.radialSegments !== currentProps.radialSegments);
        }
    }, materialId);
}
//# sourceMappingURL=nucleotide-ring-mesh.js.map